import { ScheduleTaskType } from './enums';
/**
 * 进度表的结构化数据（CR-080）。
 *
 * 客户原话「转换成 llm 模型更容易理解的格式。之后 AI 或者脚本需要根据 gantt chart 追踪 / 更新任务进度」。
 * 所以这份数据只有一份：甘特图按它画，给 AI / 脚本的 JSON 也是它 —— 两份分开存迟早对不上。
 *
 * 原表（PDF / 图片）是证据，照旧走图纸的版本链、永远不改；这里是「从原表读出来、人核对过」的那一层。
 * LLM 读不懂的从来不是格式，是原表里没写出来的东西 —— 年份没印、颜色代表什么没写、
 * 周末算不算工期没说、同一项跨周被切成三段。下面每个字段都是把这些写明。
 *
 * 推算出来的东西（每项几个工作日、哪天停工）**读的时候算，不落库**，和打卡同一个原则（约束 1）。
 */
/** 给 AI / 脚本的 JSON 带的格式版本。字段改了意思就换版本号，别悄悄改 */
export const SCHEDULE_SCHEMA = 'construction.schedule/v1';
/** 一版进度表最多多少项。Buildertrend 一个 $50 万的项目三五十项，几百项是 P6 那种专业排程 */
export const SCHEDULE_MAX_TASKS = 500;
export const SCHEDULE_NAME_MAX = 200;
/** 整个工期最长多少天。挡的是手滑把 2027 敲成 2072 */
export const SCHEDULE_MAX_SPAN_DAYS = 366 * 5;
/** 默认周一到周五上班（`Date.getUTCDay()` 的 0–6，0 是周日） */
export const DEFAULT_WORKDAYS = [1, 2, 3, 4, 5];
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;
/** `YYYY-MM-DD` → 当天 UTC 零点的毫秒数；不是合法日期给 null（2026-02-30 也算不合法） */
export function parseDateOnly(value) {
    if (!DATE_RE.test(value))
        return null;
    const ms = Date.parse(`${value}T00:00:00Z`);
    if (Number.isNaN(ms))
        return null;
    return new Date(ms).toISOString().slice(0, 10) === value ? ms : null;
}
export function formatDateOnly(ms) {
    return new Date(ms).toISOString().slice(0, 10);
}
/**
 * 按名称猜类型，给解析出来的草稿一个起点；**人在核对表里改**。
 * 「XX Inspection」是美国工地的通行叫法；「Christmas - Off」这种以 Off 结尾的是节假日。
 */
export function guessScheduleTaskType(name) {
    const n = name.trim();
    if (/[-–—]\s*off$/i.test(n) || /^(holiday|day off)\b/i.test(n))
        return ScheduleTaskType.NONWORK;
    if (/\binspections?\b/i.test(n) || /检查|验收/.test(n))
        return ScheduleTaskType.INSPECTION;
    return ScheduleTaskType.WORK;
}
/**
 * 交上来的这份进度表能不能存（CR-080）。
 *
 * **核对表当场查、服务端存之前再查一次，用的是同一份**（和补录打卡 `checkManualPunches` 一个做法）。
 * 一行只报第一个问题 —— 表上标红一格就够人改了。
 */
export function checkScheduleTasks(tasks) {
    const rows = [];
    tasks.forEach((task, index) => {
        const name = task.name?.trim() ?? '';
        const start = parseDateOnly(task.plannedStart ?? '');
        const finish = parseDateOnly(task.plannedFinish ?? '');
        let problem = null;
        if (!name)
            problem = 'nameRequired';
        else if (name.length > SCHEDULE_NAME_MAX)
            problem = 'nameTooLong';
        else if (start === null)
            problem = 'startInvalid';
        else if (finish === null)
            problem = 'finishInvalid';
        else if (finish < start)
            problem = 'order';
        else if ((finish - start) / DAY_MS > SCHEDULE_MAX_SPAN_DAYS)
            problem = 'spanTooLong';
        if (problem)
            rows.push({ index, problem });
    });
    // 整份的跨度也要挡：一项 2026、一项 2072，各自合法，放一起甘特图就被拉成一条线
    const starts = tasks.map((t) => parseDateOnly(t.plannedStart ?? '')).filter((v) => v !== null);
    const finishes = tasks.map((t) => parseDateOnly(t.plannedFinish ?? '')).filter((v) => v !== null);
    if (rows.length === 0 && starts.length > 0 && finishes.length > 0) {
        const span = (Math.max(...finishes) - Math.min(...starts)) / DAY_MS;
        if (span > SCHEDULE_MAX_SPAN_DAYS) {
            const last = tasks.findIndex((t) => parseDateOnly(t.plannedFinish) === Math.max(...finishes));
            rows.push({ index: last, problem: 'spanTooLong' });
        }
    }
    return { tooMany: tasks.length > SCHEDULE_MAX_TASKS, rows };
}
/**
 * 停工日：停工项覆盖到的**工作日**（周末本来就不上班，不用重复算）。
 * 读的时候算：停工项改了，停工日跟着变，不会有一份存着的日期表忘了改。
 */
export function scheduleNonWorkDates(tasks, workdays = DEFAULT_WORKDAYS) {
    const out = new Set();
    for (const task of tasks) {
        if (task.type !== ScheduleTaskType.NONWORK)
            continue;
        const start = parseDateOnly(task.plannedStart);
        const finish = parseDateOnly(task.plannedFinish);
        if (start === null || finish === null)
            continue;
        for (let d = start; d <= finish; d += DAY_MS) {
            if (workdays.includes(new Date(d).getUTCDay()))
                out.add(formatDateOnly(d));
        }
    }
    return [...out].sort();
}
/**
 * 一项占几个工作日：按工作日历数，扣掉停工日。停工项本身没有工期，给 null。
 * 原表（月历视图）不写工期，这是从起止日期推的 —— 源软件里设的工期可能不一样，界面上要说明。
 */
export function scheduleTaskWorkdays(task, workdays, nonWorkDates) {
    if (task.type === ScheduleTaskType.NONWORK)
        return null;
    const start = parseDateOnly(task.plannedStart);
    const finish = parseDateOnly(task.plannedFinish);
    if (start === null || finish === null || finish < start)
        return null;
    let n = 0;
    for (let d = start; d <= finish; d += DAY_MS) {
        if (workdays.includes(new Date(d).getUTCDay()) && !nonWorkDates.has(formatDateOnly(d)))
            n++;
    }
    return n;
}
/** 日历天（含首尾） */
export function scheduleTaskCalendarDays(task) {
    const start = parseDateOnly(task.plannedStart);
    const finish = parseDateOnly(task.plannedFinish);
    if (start === null || finish === null || finish < start)
        return null;
    return (finish - start) / DAY_MS + 1;
}
/** 这一项里有几天停工（停工项自己不算）。核对表上打个星号：原表的工期可能已经扣过它了 */
export function scheduleTaskNonWorkOverlap(task, nonWorkDates) {
    if (task.type === ScheduleTaskType.NONWORK)
        return 0;
    const start = parseDateOnly(task.plannedStart);
    const finish = parseDateOnly(task.plannedFinish);
    if (start === null || finish === null)
        return 0;
    let n = 0;
    for (let d = start; d <= finish; d += DAY_MS)
        if (nonWorkDates.has(formatDateOnly(d)))
            n++;
    return n;
}
/**
 * 编号：T01、T02……给人和 AI 引用（「T09 做到 40%」）。
 * 已有的项编号不变；新加的接着最大的往后排 —— 插一行不会让后面所有编号都挪一位。
 */
export function nextScheduleTaskCode(existing) {
    const max = existing.reduce((m, code) => {
        const n = /^T(\d+)$/.exec(code);
        return n ? Math.max(m, Number(n[1])) : m;
    }, 0);
    return `T${String(max + 1).padStart(2, '0')}`;
}
/** 工作日历用给人看的写法出现在 JSON 里（`["Mon", …]`），AI 不用猜 0 是周日还是周一 */
export const WEEKDAY_CODES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
//# sourceMappingURL=schedule.js.map